/**
 * @fileoverview Hook for country and product performance data from dashboard data.
 * @module shared/hooks/dashboard/useCountryProductPerformance
 */

import { useMemo } from 'react'
import { useDashboardData } from './useDashboardData'

/**
 * Product-level performance metrics for a specific SKU within a country.
 */
export interface ProductPerformance {
  /** Product SKU identifier */
  sku: string
  /** Units sold */
  quantity: number
  /** Sales revenue */
  sales: number
  /** Advertising spend */
  adSpend: number
  /** Platform breakdown of advertising spend */
  adPlatformBreakdown: Record<string, number>
  /** Cost of goods sold */
  cogs: number
  /** Total expenses (adSpend + cogs) */
  totalExpenses: number
  /** Net profit (sales - totalExpenses) */
  profit: number
  /** Return on ad spend ratio */
  roas: number
  /** Profit margin as ratio */
  margin: number
}

/**
 * Country-level performance metrics with expandable product breakdown.
 */
export interface CountryPerformance {
  /** Country code (e.g., 'PL', 'SK') */
  country: string
  /** Total units sold in country */
  quantity: number
  /** Total sales revenue for country */
  sales: number
  /** Total advertising spend for country */
  adSpend: number
  /** Platform breakdown of advertising spend */
  adPlatformBreakdown: Record<string, number>
  /** Total cost of goods sold for country */
  cogs: number
  /** Total expenses for country */
  totalExpenses: number
  /** Total profit for country */
  profit: number
  /** Overall return on ad spend for country */
  roas: number
  /** Profit margin as ratio */
  margin: number
  /** Breakdown by individual products */
  products: ProductPerformance[]
}

/**
 * Hook result for country and product performance data.
 */
export interface UseCountryProductPerformanceResult {
  /** Array of country performance data with product breakdown */
  countries: CountryPerformance[]
  /** Loading state */
  isLoading: boolean
  /** Error state */
  isError: boolean
  /** Error message */
  error: string | null
}

/**
 * Hook to get country and product performance data from dashboard data.
 * Provides country-level totals with expandable product-level details.
 *
 * @returns Country and product performance data with loading and error states
 * @example
 * ```typescript
 * const { countries, isLoading } = useCountryProductPerformance();
 * ```
 */
export function useCountryProductPerformance(): UseCountryProductPerformanceResult {
  const { salesProductQuery } = useDashboardData()

  const dimString = (d: Record<string, unknown> | undefined, key: string): string | undefined => {
    if (!d) return undefined
    const v = d[key as keyof typeof d]
    return typeof v === 'string' ? v : undefined
  }

  const countries = useMemo((): CountryPerformance[] => {
    if (!salesProductQuery.data?.data) {
      return []
    }

    // Group data points by country
    const countryGroups = new Map<string, typeof salesProductQuery.data.data>()

    salesProductQuery.data.data.forEach(dataPoint => {
      const country = dimString(dataPoint.dimensions as Record<string, unknown>, 'country')
      if (!country) return

      if (!countryGroups.has(country)) {
        countryGroups.set(country, [])
      }
      countryGroups.get(country)!.push(dataPoint)
    })

    return (
      Array.from(countryGroups.entries())
        .map(([countryName, dataPoints]): CountryPerformance => {
          // Calculate country-level totals by aggregating all products in this country
          const countryTotals = dataPoints.reduce(
            (acc, point) => {
              const revenue = point.metrics.sales || 0
              const adSpend = point.metrics.advertising || 0
              const cogs = point.metrics.cogs || 0
              // NOTE: directCosts are intentionally excluded from this table's totals.
              // This table defines Total Expenses as Ad Spend + COGs only.
              const cm2 =
                typeof point.metrics.cm2 === 'number'
                  ? point.metrics.cm2
                  : revenue - (adSpend + cogs)

              return {
                quantity: acc.quantity + (point.metrics.quantity || 0),
                sales: acc.sales + revenue,
                adSpend: acc.adSpend + adSpend,
                cogs: acc.cogs + cogs,
                // Total expenses in this table = advertising + cogs (no direct costs)
                totalExpenses: acc.totalExpenses + adSpend + cogs,
                profit: acc.profit + cm2,
                platformBreakdown: { ...acc.platformBreakdown },
              }
            },
            {
              quantity: 0,
              sales: 0,
              adSpend: 0,
              cogs: 0,
              totalExpenses: 0,
              profit: 0,
              platformBreakdown: {} as Record<string, number>,
            }
          )

          // Aggregate platform breakdowns
          dataPoints.forEach(point => {
            const platformBreakdown = point.metrics.platformBreakdown || {}
            Object.entries(platformBreakdown).forEach(([platform, amount]) => {
              countryTotals.platformBreakdown[platform] =
                (countryTotals.platformBreakdown[platform] || 0) + amount
            })
          })

          // Calculate derived metrics
          const countryRoas =
            countryTotals.sales > 0 && countryTotals.adSpend > 0
              ? countryTotals.sales / countryTotals.adSpend
              : 0
          const countryMargin =
            countryTotals.sales > 0 ? countryTotals.profit / countryTotals.sales : 0

          // Map product-level details
          const products: ProductPerformance[] = dataPoints.map((point): ProductPerformance => {
            const revenue = point.metrics.sales || 0
            const adSpend = point.metrics.advertising || 0
            const cogs = point.metrics.cogs || 0
            // directCosts are excluded from this table's Total Expenses by design
            const totalExpenses = adSpend + cogs
            const cm2 =
              typeof point.metrics.cm2 === 'number' ? point.metrics.cm2 : revenue - totalExpenses

            return {
              sku: dimString(point.dimensions as Record<string, unknown>, 'product') || '',
              quantity: point.metrics.quantity || 0,
              sales: revenue,
              adSpend: adSpend,
              adPlatformBreakdown: point.metrics.platformBreakdown || {},
              cogs: cogs,
              totalExpenses: totalExpenses,
              profit: cm2,
              roas: revenue > 0 && adSpend > 0 ? revenue / adSpend : 0,
              margin: revenue > 0 ? cm2 / revenue : 0,
            }
          })

          return {
            country: countryName,
            quantity: countryTotals.quantity,
            sales: countryTotals.sales,
            adSpend: countryTotals.adSpend,
            adPlatformBreakdown: countryTotals.platformBreakdown,
            cogs: countryTotals.cogs,
            totalExpenses: countryTotals.totalExpenses,
            profit: countryTotals.profit,
            roas: countryRoas,
            margin: countryMargin,
            products,
          }
        })
        // Sort by sales descending for stable UX
        .sort((a, b) => b.sales - a.sales)
    )
  }, [salesProductQuery])

  return {
    countries,
    isLoading: salesProductQuery.isLoading,
    isError: salesProductQuery.isError,
    error: salesProductQuery.error?.message || null,
  }
}
